import { EventType, EventData } from '@/api/types/events';

/**
 * Helper functions for tracking events with comprehensive data
 * Provides pre-configured tracking functions for common site actions
 */

// Common interface for enhanced event data
interface EnhancedEventData {
  // Core identification
  jobId?: string;
  campaignId?: number;
  applicationId?: string;
  searchTerm?: string;
  filterCriteria?: Record<string, any>;

  // User context
  userRole?: string;
  userTier?: string;
  accountType?: string;

  // Page/UI context
  pageSection?: string;
  componentName?: string;
  buttonText?: string;
  linkText?: string;
  formName?: string;

  // Business context
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;

  // Performance metrics
  loadTime?: number;
  scrollPosition?: number;
  timeOnPage?: number;
  clickPosition?: { x: number; y: number };

  // Additional metadata
  experimentVariant?: string;
  featureFlag?: string;
  error?: {
    code?: string;
    message?: string;
    stack?: string;
  };
}

/**
 * Enhanced event tracking function that automatically enriches events with maximum data
 */
export const createEnhancedEventData = (
  eventType: EventType,
  basicData: Partial<EventData> = {},
  enhancedData: EnhancedEventData = {}
): Partial<EventData> => {
  const timestamp = Math.floor(Date.now() / 1000);

  // Extract URL parameters
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();

  // Get device info
  const deviceInfo = typeof window !== 'undefined' ? {
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    color_depth: window.screen.colorDepth,
    pixel_ratio: window.devicePixelRatio
  } : {};

  // Get location info if available (from geolocation API or IP)
  // This could be expanded to include geolocation data in the future

  return {
    event_type: eventType,
    timestamp,

    // URL and UTM parameters
    utm_source: enhancedData.source || urlParams.get('utm_source') || basicData.utm_source,
    utm_medium: enhancedData.medium || urlParams.get('utm_medium') || basicData.utm_medium,
    utm_campaign: enhancedData.campaign || urlParams.get('utm_campaign') || basicData.utm_campaign,
    utm_content: enhancedData.content || urlParams.get('utm_content') || basicData.utm_content,
    utm_term: enhancedData.term || urlParams.get('utm_term') || basicData.utm_term,

    // Enhanced event details
    event_details: {
      ...basicData.event_details,

      // Job-related data
      ...(enhancedData.jobId && { job_id: enhancedData.jobId }),
      ...(enhancedData.applicationId && { application_id: enhancedData.applicationId }),

      // Search and filtering
      ...(enhancedData.searchTerm && { search_term: enhancedData.searchTerm }),
      ...(enhancedData.filterCriteria && { filter_criteria: enhancedData.filterCriteria }),

      // User context
      ...(enhancedData.userRole && { user_role: enhancedData.userRole }),
      ...(enhancedData.userTier && { user_tier: enhancedData.userTier }),
      ...(enhancedData.accountType && { account_type: enhancedData.accountType }),

      // UI context
      ...(enhancedData.pageSection && { page_section: enhancedData.pageSection }),
      ...(enhancedData.componentName && { component_name: enhancedData.componentName }),
      ...(enhancedData.buttonText && { button_text: enhancedData.buttonText }),
      ...(enhancedData.linkText && { link_text: enhancedData.linkText }),
      ...(enhancedData.formName && { form_name: enhancedData.formName }),

      // Performance metrics
      ...(enhancedData.loadTime && { load_time: enhancedData.loadTime }),
      ...(enhancedData.scrollPosition && { scroll_position: enhancedData.scrollPosition }),
      ...(enhancedData.timeOnPage && { time_on_page: enhancedData.timeOnPage }),
      ...(enhancedData.clickPosition && { click_position: enhancedData.clickPosition }),

      // Device information
      ...deviceInfo,

      // Experiment and feature flags
      ...(enhancedData.experimentVariant && { experiment_variant: enhancedData.experimentVariant }),
      ...(enhancedData.featureFlag && { feature_flag: enhancedData.featureFlag }),

      // Error information
      ...(enhancedData.error && { error: enhancedData.error }),

      // Timestamp for detailed analysis
      client_timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },

    // Merge with basic data
    ...basicData,
  };
};

/**
 * Pre-configured tracking functions for common site actions
 */

// Job viewing
export const trackJobView = (
  trackEvent: (data: Partial<EventData>) => void,
  jobData: {
    jobId: string;
    jobTitle?: string;
    jobCompany?: string;
    jobLocation?: string;
    experienceLevel?: string;
    searchTerm?: string;
    searchFilters?: Record<string, any>;
    position?: number; // Position in search results
  },
  context: EnhancedEventData = {}
) => {
  const eventData = createEnhancedEventData(EventType.JOB_VIEW, {
    element_text: jobData.jobTitle,
    interaction_value: jobData.position,
  }, {
    jobId: jobData.jobId,
    searchTerm: jobData.searchTerm,
    filterCriteria: jobData.searchFilters,
    pageSection: 'job_listings',
    ...context,
  });

  eventData.event_details = {
    ...eventData.event_details,
    job_title: jobData.jobTitle,
    job_company: jobData.jobCompany,
    job_location: jobData.jobLocation,
  };

  trackEvent(eventData);
};

// Job application
export const trackJobApply = (
  trackEvent: (data: Partial<EventData>) => void,
  applicationData: {
    jobId: string;
    jobTitle?: string;
    applicationMethod?: string; // 'quick_apply', 'external_redirect', 'form_submit'
    resumeUploaded?: boolean;
    coverLetterUploaded?: boolean;
    applicationId?: string;
  },
  context: EnhancedEventData = {}
) => {
  const eventData = createEnhancedEventData(EventType.JOB_APPLY, {
    element_text: `Apply to ${applicationData.jobTitle}`,
    type: applicationData.applicationMethod,
  }, {
    jobId: applicationData.jobId,
    applicationId: applicationData.applicationId,
    pageSection: 'job_application',
    ...context,
  });

  eventData.event_details = {
    ...eventData.event_details,
    job_title: applicationData.jobTitle,
    application_method: applicationData.applicationMethod,
    resume_uploaded: applicationData.resumeUploaded,
    cover_letter_uploaded: applicationData.coverLetterUploaded,
    conversion_type: 'job_application',
  };

  trackEvent(eventData);
};

// Search tracking
export const trackSearch = (
  trackEvent: (data: Partial<EventData>) => void,
  searchData: {
    searchTerm: string;
    searchType?: string; // 'job_search', 'company_search', etc.
    resultsCount?: number;
    filters?: Record<string, any>;
    sortBy?: string;
    location?: string;
    category?: string;
  },
  context: EnhancedEventData = {}
) => {
  const eventData = createEnhancedEventData(EventType.SEARCH, {
    element_text: searchData.searchTerm,
    interaction_value: searchData.resultsCount,
    type: searchData.searchType,
  }, {
    searchTerm: searchData.searchTerm,
    filterCriteria: searchData.filters,
    pageSection: 'search',
    ...context,
  });

  eventData.event_details = {
    ...eventData.event_details,
    search_type: searchData.searchType,
    results_count: searchData.resultsCount,
    filters_applied: searchData.filters,
    sort_by: searchData.sortBy,
    search_location: searchData.location,
    search_category: searchData.category,
  };

  trackEvent(eventData);
};

// Profile update tracking
export const trackProfileUpdate = (
  trackEvent: (data: Partial<EventData>) => void,
  updateData: {
    section: string; // 'personal_info', 'resume', 'preferences', etc.
    fieldsUpdated: string[];
    updateType?: string; // 'create', 'update', 'delete'
    profileCompleteness?: number; // 0-100
  },
  context: EnhancedEventData = {}
) => {
  const eventData = createEnhancedEventData(EventType.PROFILE_UPDATE, {
    element_text: `Update ${updateData.section}`,
    type: updateData.updateType,
    interaction_value: updateData.profileCompleteness,
  }, {
    pageSection: updateData.section,
    formName: `profile_${updateData.section}`,
    ...context,
  });

  eventData.event_details = {
    ...eventData.event_details,
    profile_section: updateData.section,
    fields_updated: updateData.fieldsUpdated,
    update_type: updateData.updateType,
    profile_completeness: updateData.profileCompleteness,
  };

  trackEvent(eventData);
};

// Unsubscribe tracking
export const trackUnsubscribe = (
  trackEvent: (data: Partial<EventData>) => void,
  unsubscribeData: {
    emailType?: string; // 'newsletter', 'job_alerts', 'marketing'
    reason?: string;
    campaignId?: number;
    mailId?: string;
    userId?: string;
  },
  context: EnhancedEventData = {}
) => {
  const eventData = createEnhancedEventData(EventType.UNSUBSCRIBE, {
    type: unsubscribeData.emailType,
    mail_id: unsubscribeData.mailId,
    user_id: unsubscribeData.userId,
    campaign_id: unsubscribeData.campaignId,
  }, {
    campaignId: unsubscribeData.campaignId,
    pageSection: 'unsubscribe',
    ...context,
  });

  eventData.event_details = {
    ...eventData.event_details,
    email_type: unsubscribeData.emailType,
    unsubscribe_reason: unsubscribeData.reason,
  };

  trackEvent(eventData);
};

// Generic click tracking with enhanced data
export const trackEnhancedClick = (
  trackEvent: (data: Partial<EventData>) => void,
  clickData: {
    elementId?: string;
    elementClass?: string;
    elementText?: string;
    linkUrl?: string;
    buttonType?: string;
  },
  context: EnhancedEventData = {}
) => {
  const eventData = createEnhancedEventData(EventType.CLICK, {
    element_id: clickData.elementId,
    element_class: clickData.elementClass,
    element_text: clickData.elementText,
    url: clickData.linkUrl,
    type: clickData.buttonType,
  }, context);

  trackEvent(eventData);
};

// Form submission tracking
export const trackFormSubmit = (
  trackEvent: (data: Partial<EventData>) => void,
  formData: {
    formName: string;
    formFields?: string[];
    formAction?: string;
    validationErrors?: string[];
    completionTime?: number; // Time taken to fill form in seconds
  },
  context: EnhancedEventData = {}
) => {
  const eventData = createEnhancedEventData(EventType.SUBMIT_FORM, {
    element_id: formData.formName,
    element_text: `Submit ${formData.formName}`,
    interaction_value: formData.completionTime,
  }, {
    formName: formData.formName,
    ...context,
  });

  eventData.event_details = {
    ...eventData.event_details,
    form_name: formData.formName,
    form_fields: formData.formFields,
    form_action: formData.formAction,
    validation_errors: formData.validationErrors,
    completion_time: formData.completionTime,
  };

  trackEvent(eventData);
};